import { AfterViewInit, Component, Input } from '@angular/core';
import { BlockUI, NgBlockUI }              from 'ng-block-ui';
import { NgForm }                          from '@angular/forms';
import { TranslateService }                from '@ngx-translate/core';
import { UIRouter }                        from '@uirouter/core';

import { CodeTypeModel }             from '../core/models/code-type';
import { MessagesService }           from '../messages/messages.service';
import { PriceMatrixItemModel }      from './typings/price-matrix.item';
import { PriceMatrixModel }          from './typings/price-matrix.matrix';
import { PriceMatrixService }        from './price-matrix.service';
import { PriceMatrixUpdateModel }    from './typings/price-matrix.update-matrix';
import { RemoveComponent }           from '../table/cell-components/actions/remove.component';
import { Table }                     from '../table/types/table';
import { TableColumn }               from '../table/types/table-column';
import { TableTextControlComponent } from '../table/cell-components/controls/text-control/table-text-control.component';

@Component({
  selector   : 'wor-price-matrix-settings',
  template: require('./price-matrix.settings.component.html')
})
export class PriceMatrixComponent implements AfterViewInit {
  @BlockUI('priceMatrixSettingsBlock') block: NgBlockUI;

  @Input() matrices   : Array<PriceMatrixModel>;
  @Input() types      : Array<CodeTypeModel>;
  @Input() updateTypes: Array<CodeTypeModel>;

  items: Array<PriceMatrixItemModel> = [];

  matrix = {} as PriceMatrixModel;

  tierErrorCells = new Set<string>();

  groupTableSettings: Table = {
    columns: [
      this.buildRemoveColumn(),
      {
        field : 'product_group_name',
        header: {
          text: this.translateService.instant('PRODUCT_SPACE.PRODUCT_GROUP')
        }
      },
      {
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      this.buildPercentageColumn(),
    ],
    empty: {
      text: this.translateService.instant('VEHICLE_SPACE.NO_TYPES_FOR_DESCRIPTION', {
        name: this.matrix ? this.matrix.matrix_name : ''
      })
    }
  };

  tierTableSettings: Table = {
    columns: [
      this.buildRemoveColumn(),
      {
        component: {
          bindings: {
            inputs: {
              model: 'product_group_name',
              name : 'product_group_name',
              type : 'text'
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      this.buildTierRangeColumn('unit_price_start', 'PRICE_MATRIX_SPACE.UNIT_PRICE_START'),
      this.buildTierRangeColumn('unit_price_end', 'PRICE_MATRIX_SPACE.UNIT_PRICE_END'),
      this.buildPercentageColumn(),
    ],
    empty: {
      text: this.translateService.instant('VEHICLE_SPACE.NO_TYPES_FOR_DESCRIPTION', {
        name: this.matrix ? this.matrix.matrix_name : ''
      })
    }
  };

  updateMatrix: PriceMatrixUpdateModel = {
    updateType: '*',
    percentage: 0
  };

  get tableSettings () : Table {
    return this.isPriceTier() ? this.tierTableSettings : this.groupTableSettings;
  }

  get typeOptions () : Array<CodeTypeModel> {
    return this.isPriceTier()
      ? this.types
      : this.types.filter(( type : CodeTypeModel ) => type.code !== 'B');
  }

  constructor (
    private messagesService   : MessagesService,
    public  priceMatrixService: PriceMatrixService,
    private router            : UIRouter,
    private translateService  : TranslateService
  ) {}

  ngAfterViewInit () : void {
    if ( this.matrices.length ) {
      this.matrix = Object.assign({}, this.matrices[ 0 ]);

      this.getItems();
    }
  }

  add () : void {
    this.priceMatrixService.add()
    .then(( matrix : PriceMatrixModel ) => this.priceMatrixService.get()
      .then(( matrices : Array<PriceMatrixModel> ) => {
        this.matrices = matrices;
        this.matrix   = matrix;
      }))
    .then(() => this.getItems())
    .catch(error => this.messagesService.error(error));
  }

  addAllProductGroups () : void {
    this.priceMatrixService.addAllProductGroups(this.matrix)
    .then(( items : Array<PriceMatrixItemModel> ) => this.items = items)
    .catch(error => this.messagesService.error(error));
  }

  addMarkupRange () : void {
    this.priceMatrixService.addMarkupRange(this.matrix)
    .then(( items : Array<PriceMatrixItemModel> ) => this.items = items)
    .catch(error => this.messagesService.error(error));
  }

  addProductGroup () : void {
    this.priceMatrixService.addGroup(this.matrix)
    .then(( items : Array<PriceMatrixItemModel> ) => this.items = items)
    .catch(error => this.messagesService.error(error));
  }

  buildPercentageColumn () : TableColumn {
    return {
      classes  : 'text-right',
      component: {
        bindings: {
          inputs: {
            model: 'percentage',
            name : 'percentage',
            type : 'number'
          }
        },
        enabled: true,
        factory: TableTextControlComponent
      },
      header: {
        classes: 'text-right',
        text   : this.translateService.instant('PRICE_MATRIX_SPACE.PERCENTAGE')
      }
    };
  }

  buildRemoveColumn () : TableColumn {
    return {
      component: {
        bindings: {
          outputs: {
            onRemoveClick: ( item : PriceMatrixItemModel ) => this.deleteItem(item)
          }
        },
        enabled: true,
        factory: RemoveComponent,
      },
      sortable: false,
    };
  }

  buildTierRangeColumn ( field : string, label : string ) : TableColumn {
    return {
      classes  : 'text-right',
      component: {
        bindings: {
          inputs: {
            invalid: ( item : PriceMatrixItemModel ) => this.tierErrorCells.has(`${item.id}:${field}`),
            model  : field,
            name   : field,
            type   : 'number'
          }
        },
        enabled: true,
        factory: TableTextControlComponent
      },
      header: {
        classes: 'text-right',
        text   : this.translateService.instant(label)
      }
    };
  }

  canSave () : boolean {
    if ( !this.isPriceTier() ) {
      return true;
    }

    const error = this.getTierError();

    if ( error ) {
      this.messagesService.show(this.translateService.instant(error), 'error');
    }

    return !error;
  }

  delete () : void {
    this.priceMatrixService.delete(this.matrix)
    .then(() => {
      this.messagesService.show(this.translateService.instant('PRICE_MATRIX_SPACE.MATRIX_DELETED', {
        code: this.matrix.matrix_name
      }), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  deleteItem ( item : PriceMatrixItemModel ) : void {
    this.priceMatrixService.deleteItem(item)
    .then(( items : Array<PriceMatrixItemModel> ) => {
      this.messagesService.show(this.translateService.instant('PRICE_MATRIX_SPACE.ITEM_DELETED', {
        code: item.product_group_name
      }), 'success');

      this.items = items;
    })
    .catch(error => this.messagesService.error(error));
  }


  getItems () : void {
    this.tierErrorCells.clear();

    this.tableSettings.empty.text = this.translateService.instant('VEHICLE_SPACE.NO_TYPES_FOR_DESCRIPTION', {
        name: this.matrix ? this.matrix.matrix_name : ''
      });

    if ( this.matrix.id ) {
      this.block.start();

      this.priceMatrixService.getItems(this.matrix)
      .then(( items : Array<PriceMatrixItemModel> ) => this.items = items)
      .catch(error => this.messagesService.error(error))
      .finally(() => this.block.stop());
    } else {
      this.items = [];
    }
  }

  getTierError () : string {
    const tiers = this.items
      .map(( item : PriceMatrixItemModel ) => ({
        end  : Number(item.unit_price_end),
        id   : item.id,
        start: Number(item.unit_price_start)
      }))
      .sort(( a, b ) => a.start - b.start);

    let error    = null;
    let previous = null;

    this.tierErrorCells.clear();

    for ( const tier of tiers ) {
      if ( tier.start < 0.01 ) {
        this.tierErrorCells.add(`${tier.id}:unit_price_start`);

        error = error || 'PRICE_MATRIX_SPACE.TIER_START_MINIMUM';
      }

      if ( tier.end <= tier.start ) {
        this.tierErrorCells.add(`${tier.id}:unit_price_start`);
        this.tierErrorCells.add(`${tier.id}:unit_price_end`);

        error = error || 'PRICE_MATRIX_SPACE.TIER_END_GREATER';
      }

      if ( previous && tier.start <= previous.end ) {
        this.tierErrorCells.add(`${previous.id}:unit_price_end`);
        this.tierErrorCells.add(`${tier.id}:unit_price_start`);

        error = error || 'PRICE_MATRIX_SPACE.TIERS_CANNOT_OVERLAP';
      }

      previous = tier;
    }

    return error;
  }

  isPriceTier () : boolean {
    return this.matrix.matrix_type === 'B';
  }

  onChange ( id : string ) : void {
    this.matrix = Object.assign({}, this.matrices.find(( matrix : PriceMatrixModel ) => matrix.id === id));

    this.getItems();
  }

  pushToOtherBranches () : void {
    if ( !this.canSave() ) {
      return;
    }

    this.priceMatrixService.update(this.matrix, this.items)
    .then(( data : { matrix : PriceMatrixModel; matrix_item : Array<PriceMatrixItemModel> } ) => {
      this.matrix = data.matrix;
      this.items  = data.matrix_item;

      return;
    })
    .then(() => this.priceMatrixService.pushToOtherBranches(this.matrix))
    .then(( message : string ) => this.messagesService.show(message, 'success'))
    .catch(error => this.messagesService.error(error));
  }

  save ( form : NgForm ) : void {
    if ( !this.canSave() ) {
      return;
    }

    this.block.start();

    this.priceMatrixService.update(this.matrix, this.items)
    .then(( data : { matrix : PriceMatrixModel; matrix_item : Array<PriceMatrixItemModel> } ) => {
      this.matrix = data.matrix;
      this.items  = data.matrix_item;
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  updatePercentages () : void {
    this.priceMatrixService.updatePercentages(this.matrix, this.updateMatrix)
    .then(() => {
      this.messagesService.show(this.translateService.instant('PRICE_MATRIX_SPACE.PERCENTAGES_UPDATED'), 'success');

      this.getItems();
    })
    .catch(error => this.messagesService.error(error));
  }
}
